import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Slider,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Switch,
  FormControlLabel,
  Collapse,
  Chip,
  Tooltip,
} from '@mui/material'
import {
  Save,
  Cancel,
  MyLocation,
  Visibility,
  VisibilityOff,
  People,
  Group,
  Groups,
  Explore,
  ExpandMore,
  ExpandLess,
  Shield,
} from '@mui/icons-material'
import { settingsApi } from '@api/endpoints/settings'
import { GeofencePicker } from '@components/map/GeofencePicker'
import { ErrorDialog } from '@components/ErrorDialog'
import { getSaveFriendlyError, type FriendlyError } from '@utils/errorHandling'

interface BillingAddress {
  addressLine1: string
  addressLine2: string
  city: string
  stateOrProvince: string
  postalCode: string
  country: string
}

interface BillingAddressData {
  billingAddress: BillingAddress
  homeLatitude: number | null
  homeLongitude: number | null
  locationSharingMinHideDistanceMiles: number | null
}

// Conversion factor
const MILES_TO_KM = 1.60934
const KM_TO_MILES = 1 / MILES_TO_KM

export function BillingAddressSection() {
  const [billingAddress, setBillingAddress] = useState<BillingAddress>({
    addressLine1: '',
    addressLine2: '',
    city: '',
    stateOrProvince: '',
    postalCode: '',
    country: '',
  })
  const [homeLocation, setHomeLocation] = useState<{ latitude: number; longitude: number } | null>(null)
  const [minHideDistance, setMinHideDistance] = useState<number>(0) // Always stored in miles internally
  const [measurementUnit, setMeasurementUnit] = useState<number>(1) // 0 = Metric (km), 1 = Imperial (miles)
  const [showHometown, setShowHometown] = useState<boolean>(true)
  const [showLocationToFriends, setShowLocationToFriends] = useState<boolean>(false)
  const [showLocationToGroups, setShowLocationToGroups] = useState<boolean>(false)
  const [showLocationToAdventureParticipants, setShowLocationToAdventureParticipants] = useState<boolean>(false)
  const [visibilityMenuAnchor, setVisibilityMenuAnchor] = useState<null | HTMLElement>(null)
  const [sharingPanelExpanded, setSharingPanelExpanded] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isGeocoding, setIsGeocoding] = useState(false)
  const [error, setError] = useState<FriendlyError | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [hasUserEditedAddress, setHasUserEditedAddress] = useState(false)

  // Load billing address and home location
  useEffect(() => {
    loadSettings()
  }, [])

  // Auto-geocode when address changes (debounced)
  // Only geocode after user has edited the address fields
  useEffect(() => {
    // Skip geocoding if user hasn't edited the address
    if (!hasUserEditedAddress) {
      return
    }

    // Only geocode if we have at least city and country
    if (!billingAddress.city || !billingAddress.country) {
      return
    }

    // Debounce the geocoding to avoid excessive API calls
    const timeoutId = setTimeout(() => {
      geocodeAddress()
    }, 1000) // Wait 1 second after user stops typing

    return () => clearTimeout(timeoutId)
  }, [
    billingAddress.addressLine1,
    billingAddress.city,
    billingAddress.stateOrProvince,
    billingAddress.postalCode,
    billingAddress.country,
    hasUserEditedAddress,
  ])

  const loadSettings = async () => {
    try {
      setIsLoading(true)
      const data = await settingsApi.getBillingAddress()
      const preferences = await settingsApi.getUserPreferences()
      const locationSharing = await settingsApi.getLocationSharingSettings()

      setBillingAddress(data.billingAddress || {
        addressLine1: '',
        addressLine2: '',
        city: '',
        stateOrProvince: '',
        postalCode: '',
        country: '',
      })

      if (data.homeLatitude && data.homeLongitude) {
        setHomeLocation({
          latitude: data.homeLatitude,
          longitude: data.homeLongitude,
        })
      }

      // Set measurement unit preference
      setMeasurementUnit(preferences.measurementUnit ?? 1) // Default to Imperial

      // Always store in miles internally, API already returns miles
      const distanceInMiles = data.locationSharingMinHideDistanceMiles ?? 0
      setMinHideDistance(distanceInMiles)

      // Load location sharing settings
      setShowHometown(locationSharing.showHometown ?? true)
      setShowLocationToFriends(locationSharing.showLocationToFriends ?? false)
      setShowLocationToGroups(locationSharing.showLocationToGroups ?? false)
      setShowLocationToAdventureParticipants(locationSharing.showLocationToAdventureParticipants ?? false)
    } catch (err) {
      setError(getSaveFriendlyError(err))
    } finally {
      setIsLoading(false)
    }
  }

  const handleFieldChange = (field: keyof BillingAddress) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setBillingAddress({
      ...billingAddress,
      [field]: e.target.value,
    })
    setSuccessMessage(null)
    // Mark that user has edited the address to enable geocoding
    setHasUserEditedAddress(true)
  }

  const handleLocationChange = (location: { latitude: number; longitude: number } | undefined) => {
    if (location) {
      setHomeLocation(location)
    } else {
      setHomeLocation(null)
    }
    setSuccessMessage(null)
  }

  const geocodeAddress = async () => {
    // Build address string for geocoding
    const addressParts = [
      billingAddress.addressLine1,
      billingAddress.city,
      billingAddress.stateOrProvince,
      billingAddress.postalCode,
      billingAddress.country,
    ].filter(Boolean)

    if (addressParts.length === 0) {
      return // Silently skip if no address
    }

    const addressString = addressParts.join(', ')

    try {
      setIsGeocoding(true)

      // Use Nominatim geocoding service (OpenStreetMap)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(addressString)}&limit=1`
      )

      if (!response.ok) {
        throw new Error('Geocoding service unavailable')
      }

      const data = await response.json()

      if (data && data.length > 0) {
        const location = {
          latitude: parseFloat(data[0].lat),
          longitude: parseFloat(data[0].lon),
        }
        setHomeLocation(location)
      }
    } catch (err) {
      // Silently fail - user can still click on map
      console.warn('Geocoding failed:', err)
    } finally {
      setIsGeocoding(false)
    }
  }

  const handleSave = async () => {
    try {
      setIsSaving(true)
      setError(null)
      setSuccessMessage(null)

      // minHideDistance is already stored in miles internally, no conversion needed
      // Save billing address and home location
      await settingsApi.updateBillingAddress({
        billingAddress,
        homeLatitude: homeLocation?.latitude || null,
        homeLongitude: homeLocation?.longitude || null,
        locationSharingMinHideDistanceMiles: minHideDistance,
      })

      // Save location sharing settings
      await settingsApi.updateLocationSharingSettings({
        showHometown,
        showLocationToFriends,
        showLocationToGroups,
        showLocationToAdventureParticipants,
      })

      setSuccessMessage('Billing address and home location updated successfully!')
      await loadSettings()
    } catch (err) {
      setError(getSaveFriendlyError(err))
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      {/* Error Dialog */}
      {error && (
        <ErrorDialog
          open={true}
          title={error.title}
          message={error.message}
          details={error.details}
          onClose={() => setError(null)}
        />
      )}

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Billing Address - Compact */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
          Billing Address
        </Typography>

        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Address Line 1"
              value={billingAddress.addressLine1}
              onChange={handleFieldChange('addressLine1')}
              placeholder="Street address"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Address Line 2"
              value={billingAddress.addressLine2}
              onChange={handleFieldChange('addressLine2')}
              placeholder="Apt, suite, etc."
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="City"
              value={billingAddress.city}
              onChange={handleFieldChange('city')}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                label="State / Province"
                value={billingAddress.stateOrProvince}
                onChange={handleFieldChange('stateOrProvince')}
                required
              />
              <IconButton
                onClick={(e) => setVisibilityMenuAnchor(e.currentTarget)}
                size="small"
                title="Hometown visibility"
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                {showHometown ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
              </IconButton>
            </Box>
          </Grid>

          {/* Visibility Menu */}
          <Menu
            anchorEl={visibilityMenuAnchor}
            open={Boolean(visibilityMenuAnchor)}
            onClose={() => setVisibilityMenuAnchor(null)}
          >
            <MenuItem
              onClick={() => {
                setShowHometown(true)
                setVisibilityMenuAnchor(null)
                setSuccessMessage(null)
              }}
            >
              <ListItemIcon>
                <Visibility fontSize="small" />
              </ListItemIcon>
              <ListItemText>Show Hometown</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                setShowHometown(false)
                setVisibilityMenuAnchor(null)
                setSuccessMessage(null)
              }}
            >
              <ListItemIcon>
                <VisibilityOff fontSize="small" />
              </ListItemIcon>
              <ListItemText>Hide Hometown</ListItemText>
            </MenuItem>
          </Menu>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Postal Code"
              value={billingAddress.postalCode}
              onChange={handleFieldChange('postalCode')}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Country"
              value={billingAddress.country}
              onChange={handleFieldChange('country')}
              required
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Home Location with Integrated Controls */}
      <Paper elevation={0} sx={{ p: 0, mb: 2, border: '1px solid', borderColor: 'divider', position: 'relative', overflow: 'hidden' }}>
        {/* Map with Overlays */}
        <Box sx={{ position: 'relative' }}>
          <GeofencePicker
            center={homeLocation ?? undefined}
            radiusMiles={minHideDistance}
            onChange={(location) => handleLocationChange(location)}
            height="450px"
            showRadius={minHideDistance > 0}
            radiusColor="#f44336"
          />

          {/* Privacy Distance Slider Overlay (Bottom) */}
          <Paper
            elevation={3}
            sx={{
              position: 'absolute',
              bottom: 10,
              left: '50%',
              transform: 'translateX(-50%)',
              px: 3,
              py: 1.5,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(4px)',
              borderRadius: 2,
              minWidth: '300px',
              maxWidth: '90%',
              zIndex: 1001, // Higher than map controls
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Privacy Zone:
              </Typography>
              <Typography variant="caption" color="primary">
                {measurementUnit === 0
                  ? `${Math.round(minHideDistance * MILES_TO_KM)} km`
                  : `${minHideDistance} mi`
                }
              </Typography>
            </Box>
            <Slider
              value={minHideDistance}
              onChange={(_, value) => {
                setMinHideDistance(value as number)
                setSuccessMessage(null)
              }}
              min={0}
              max={25}
              step={1}
              size="small"
              valueLabelDisplay="auto"
              valueLabelFormat={(value) =>
                measurementUnit === 0
                  ? `${Math.round(value * MILES_TO_KM)} km`
                  : `${value} mi`
              }
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.7rem' }}>
              {minHideDistance === 0
                ? 'Location sharing enabled (subject to privacy settings)'
                : 'Location hidden when within this distance from home'}
            </Typography>
          </Paper>

          {/* Location Sharing Overlay (Top Right - Centered under Map's Location Button) */}
          <Box
            sx={{
              position: 'absolute',
              top: 60, // Position below the "Use My Location" button (10px top + 40px button height + 10px gap)
              right: 10, // Same as location button to align centers
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              zIndex: 1001,
            }}
          >
            {/* Privacy Shield Button - Circular to match map controls */}
            <Tooltip title="Privacy Settings" placement="left">
              <IconButton
                onClick={() => setSharingPanelExpanded(!sharingPanelExpanded)}
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: 'background.paper',
                  boxShadow: 2,
                  '&:hover': {
                    backgroundColor: 'background.paper',
                    boxShadow: 4,
                  },
                }}
              >
                <Shield color={sharingPanelExpanded ? 'primary' : 'action'} />
              </IconButton>
            </Tooltip>

            {/* Expandable Sharing Controls Panel */}
            <Collapse in={sharingPanelExpanded} sx={{ position: 'absolute', top: 50, right: 0 }}>
              <Paper
                elevation={3}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(4px)',
                  borderRadius: 2,
                  overflow: 'hidden',
                  width: 250,
                }}
              >
                <Box sx={{ p: 1.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                    Share Location With:
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Switch
                        size="small"
                        checked={showLocationToFriends}
                        onChange={(e) => {
                          setShowLocationToFriends(e.target.checked)
                          setSuccessMessage(null)
                        }}
                      />
                      <People fontSize="small" color="action" />
                      <Box>
                        <Typography variant="body2" sx={{ lineHeight: 1.2 }}>
                          Friends
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Direct friends only
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Switch
                        size="small"
                        checked={showLocationToGroups}
                        onChange={(e) => {
                          setShowLocationToGroups(e.target.checked)
                          setSuccessMessage(null)
                        }}
                      />
                      <Groups fontSize="small" color="action" />
                      <Box>
                        <Typography variant="body2" sx={{ lineHeight: 1.2 }}>
                          Groups
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Joined groups
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Switch
                        size="small"
                        checked={showLocationToAdventureParticipants}
                        onChange={(e) => {
                          setShowLocationToAdventureParticipants(e.target.checked)
                          setSuccessMessage(null)
                        }}
                      />
                      <Explore fontSize="small" color="action" />
                      <Box>
                        <Typography variant="body2" sx={{ lineHeight: 1.2 }}>
                          Adventures
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Active adventures
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Collapse>
          </Box>
        </Box>
      </Paper>

      {/* Action Buttons */}
      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button variant="outlined" onClick={loadSettings} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={16} /> : <Save />}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>
    </Box>
  )
}
